import { apiClient } from '../../core/api';
import { insightsDateRangePresets } from './dateRangePresets';

const padTwo = (value: number) => String(value).padStart(2, '0');

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const toUtcDateRange = (startDate: string, endDate: string): { startTime: string; endTime: string } => {
  const start = parseLocalDate(startDate);
  start.setHours(0, 0, 0, 0);
  const end = parseLocalDate(endDate);
  end.setDate(end.getDate() + 1);
  end.setHours(0, 0, 0, 0);
  return { startTime: start.toISOString(), endTime: end.toISOString() };
};

export interface MostAskedQuestionRecord {
  classification: string;
  count: string;
  notMissedCount: string;
  input?: string;
  output?: string;
  sessionId?: string;
  spanId?: string;
}

export interface MissedQuestionRecord {
  classification: string;
  count: string;
  missedCount?: string;
  input?: string;
  output?: string;
  sessionId?: string;
  spanId?: string;
}

export interface InsightsFilters {
  startTime: string; // YYYY-MM-DD format
  endTime: string; // YYYY-MM-DD format
  userId: string;
}

interface MostAskedQuestionsResponse {
  data: {
    mostAskedQuestionV2: {
      edges: Array<{
        node: MostAskedQuestionRecord;
        __typename: string;
      }>;
      __typename: string;
    };
  };
}

interface MissedQuestionsResponse {
  data: {
    missedQuestionV2: {
      edges: Array<{
        node: MissedQuestionRecord;
        __typename: string;
      }>;
      __typename: string;
    };
  };
}

export class InsightsService {
  /**
   * Get most asked questions from GraphQL
   */
  static async getMostAskedQuestions(filters: InsightsFilters): Promise<MostAskedQuestionRecord[]> {
    const query = `
      query MostAskedQuestions($userId: String!, $startTime: Datetime!, $endTime: Datetime!) {
        mostAskedQuestionV2(userId: $userId, startTime: $startTime, endTime: $endTime) {
          edges {
            node {
              classification
              count
              notMissedCount
              input
              output
              sessionId
              spanId
              __typename
            }
            __typename
          }
          __typename
        }
      }
    `;

    try {
      const payload = {
        operationName: 'MostAskedQuestions',
        variables: {
          userId: filters.userId,
          ...toUtcDateRange(filters.startTime, filters.endTime),
        },
        query,
      };

      const response = await apiClient.graphql<MostAskedQuestionsResponse['data']>(
        payload,
        '/graphql'
      );

      // Dev's GraphQL layer answers this query with an envelope carrying NEITHER data NOR errors
      // for tenants whose conversations have never been classified (seen 2026-08-11, tenant gtg).
      // apiClient.graphql only throws on `errors`, so an unguarded read here turned "no insights
      // yet" into a TypeError banner across the whole sheet. No rows is a state, not a crash.
      // Real failures still throw: GraphQL errors throw in apiClient, HTTP/network errors reject.
      return response.data?.mostAskedQuestionV2?.edges?.map(edge => edge.node) ?? [];
    } catch (error) {
      console.error('Failed to fetch most asked questions:', error);
      throw error;
    }
  }

  /**
   * Get missed questions from GraphQL
   */
  static async getMissedQuestions(filters: InsightsFilters): Promise<MissedQuestionRecord[]> {
    const query = `
      query MissedQuestions($userId: String!, $startTime: Datetime!, $endTime: Datetime!) {
        missedQuestionV2(userId: $userId, startTime: $startTime, endTime: $endTime) {
          edges {
            node {
                classification
                count
                missedCount
                input
                output
                sessionId
                spanId
              __typename
            }
            __typename
          }
          __typename
        }
      }
    `;

    try {
      const payload = {
        operationName: 'MissedQuestions',
        variables: {
          userId: filters.userId,
          ...toUtcDateRange(filters.startTime, filters.endTime),
        },
        query,
      };

      const response = await apiClient.graphql<MissedQuestionsResponse['data']>(
        payload,
        '/graphql'
      );

      // Same no-data/no-errors envelope as mostAskedQuestionV2 above — empty, not a crash.
      return response.data?.missedQuestionV2?.edges?.map(edge => edge.node) ?? [];
    } catch (error) {
      console.error('Failed to fetch missed questions:', error);
      throw error;
    }
  }

  /**
   * Get date range presets
   */
  /** Delegates to ./dateRangePresets — see that file for the import cycle it breaks. */
  static getDateRangePresets(): { [key: string]: { startTime: string; endTime: string } } {
    return insightsDateRangePresets();
  }
}
